<template>
  <div v-if="show" class="modal-overlay" @click="handleModalClick">
    <div class="modal-content" @click.stop>
      <button class="close-button" @click="handleClose">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M4.16504 4.16602L15.8309 15.8319" stroke="#5A5C62" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M4.1642 15.8319L15.8301 4.16602" stroke="#5A5C62" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <div class="modal-header">
        <h2 class="modal-title">{{ $t('jobDetails.title') }} : {{ job?.id || 'NA' }}</h2>
      </div>
      <div class="modal-body">
        <!-- Job Info Section -->
        <div class="job-info-section">
          <div class="info-grid">
            <div class="info-item">
              <div class="info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div class="info-content">
                <span class="info-label">{{ $t('jobInfo.filesProcessed') }}</span>
                <span class="info-value">{{ job?.mx_job_files?.length || 0 }}</span>
              </div>
            </div>

            <div class="info-item">
              <div class="info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <div class="info-content">
                <span class="info-label">{{ $t('jobInfo.documentsProcessed') }}</span>
                <span class="info-value">{{ job?.mx_job_result_documents?.length || 0 }}</span>
              </div>
            </div>

            <div class="info-item">
              <div class="info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="9" y1="21" x2="9" y2="9" />
                </svg>
              </div>
              <div class="info-content">
                <span class="info-label">{{ $t('jobInfo.pagesProcessed') }}</span>
                <span class="info-value">{{ job?.mx_file_page_textract_details?.length || 0 }}</span>
              </div>
            </div>

            <div class="info-item" v-if="job?.status === 'completed'">
              <div class="info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div class="info-content">
                <span class="info-label">{{ $t('jobInfo.processingTime') }}</span>
                <span class="info-value">{{ processingTime }}</span>
              </div>
            </div>
          </div>

          <div v-if="job?.status !== 'completed' && (job?.message?.length > 0 || hasFileErrors)" class="message-section error">
            <div class="message-header">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{{ $t('jobInfo.errorMessage') }}</span>
            </div>
            <div class="message-content">
              <div v-if="job?.message?.length > 0">
                <p><strong>Message:</strong></p>
                <ol>
                  <li>{{ job.message }}</li>
                </ol>
              </div>
              <div v-if="hasFileErrors" class="file-errors-section">
                <p><strong>File Errors:</strong></p>
                <ul>
                  <li v-for="(file, index) in filesWithErrors" :key="index" class="file-error-item">
                    <span v-if="file.file_name" class="info-label">{{ file.file_name }}:</span>
                    <span class="info-label">{{ file.error }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div v-if="job?.status === 'completed' && job?.message?.length > 0" class="message-section success">
            <div class="message-header">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>{{ $t('jobInfo.successMessage') }}</span>
            </div>
            <div class="message-content">
              {{ job?.message }}
            </div>
          </div>
        </div>

        <div class="table-container">
          <table class="custom-table">
            <thead class="custom-table-header">
              <tr class="custom-table-row">
                <th style="width: 30px">#</th>
                <th v-if="!isBedrock && !isTextract">{{ $t('jobDetails.table.primary_classification') }}</th>
                <th v-if="!isBedrock && !isTextract">{{ $t('jobDetails.table.sub_category') }}</th>
                <th>{{ $t('jobDetails.table.document_types') }}</th>
                <th>{{ $t('jobDetails.table.document_name') }}</th>
                <th style="width: 185px">{{ $t('jobDetails.table.uploaded_on') }}</th>
                <th>{{ $t('jobDetails.table.uploaded_by') }}</th>
                <th>{{ $t('jobDetails.table.status') }}</th>
                <th>{{ $t('jobDetails.table.vasion_status') }}</th>
                <th style="text-align: center">{{ $t('jobDetails.table.actions') }}</th>
              </tr>
            </thead>
            <tbody v-if="!isLoadingPaginatedDetails" class="custom-table-body">
              <!-- Highlighted row for selected job -->
              <tr class="highlighted-row sticky-row">
                <td class="truncate-cell" style="width: 30px">
                  <!-- {{ index + 1 }} -->
                </td>
                <td v-if="!isBedrock && !isTextract" class="truncate-cell">
                  <div class="tooltip-container">
                    <span class="truncate-text">{{ getCategoryNames(job) }}</span>
                    <div class="tooltip">{{ getCategoryNamesTooltip(job) }}</div>
                  </div>
                </td>
                <td v-if="!isBedrock && !isTextract" class="truncate-cell">
                  <div class="tooltip-container">
                    <span class="truncate-text">{{ getDepartmentNames(job) }}</span>
                    <div class="tooltip">{{ getDepartmentNamesTooltip(job) }}</div>
                  </div>
                </td>
                <td class="truncate-cell">
                  <div class="tooltip-container">
                    <span class="truncate-text">{{ newFormatString(getFullDocumentTypeNames(job)) }}</span>
                    <div class="tooltip">{{ newFormatString(getFullDocumentTypeNamesTooltip(job)) }}</div>
                  </div>
                </td>
                <td class="truncate-cell">
                  <div class="tooltip-container">
                    <span class="truncate-text">{{ getFileNames(job) }}</span>
                    <div class="tooltip">{{ getFileNamesTooltip(job) }}</div>
                  </div>
                </td>
                <td style="width: 130px">{{ formatDate(job?.created_at).date }}/{{ formatDate(job?.created_at).time }}</td>
                <td class="truncate-cell">
                  <div class="tooltip-container">
                    <span class="truncate-text">{{ getCreatedBy(job) }}</span>
                  </div>
                </td>
                <td>
                  <StatusBadge :status="job?.status" />
                </td>
                <td style="text-align: left">
                  <StatusBadge :status="job?.vasion_status" />
                </td>
                <td>
                  <!-- <div class="actions-menu" style="text-align: center">
                    <button class="actions-button" style="text-align: center" @click="showActionsMenu($event, job)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="6" r="2" fill="var(--primary-color)" />
                        <circle cx="12" cy="12" r="2" fill="var(--primary-color)" />
                        <circle cx="12" cy="18" r="2" fill="var(--primary-color)" />
                      </svg>
                    </button>
                  </div> -->
                </td>
              </tr>
              <!-- Existing rows -->
              <template v-if="job?.status !== 'inprogress'">
                <tr class="normal-row" v-for="(detail, index) in paginatedDetails?.filter((e) => e.is_deleted === false)" :key="index" @click="job?.status !== 'pending' && handleView(detail, false)">
                  <td class="truncate-cell">
                    {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                  </td>
                  <td v-if="!isBedrock && !isTextract" class="truncate-cell">
                    <div class="tooltip-container">
                      <span class="truncate-text">{{ detail.category }}</span>
                      <div class="tooltip">{{ detail.category }}</div>
                    </div>
                  </td>
                  <td v-if="!isBedrock && !isTextract" class="truncate-cell">
                    <div class="tooltip-container">
                      <span class="truncate-text">{{ detail.department }}</span>
                      <div class="tooltip">{{ detail.department }}</div>
                    </div>
                  </td>
                  <td class="truncate-cell">
                    <div class="tooltip-container">
                      <span class="truncate-text">{{ newFormatString(detail.documentType) }}</span>
                      <div class="tooltip">{{ newFormatString(detail.documentType) }}</div>
                    </div>
                  </td>
                  <td class="truncate-cell">
                    <div class="tooltip-container">
                      <span class="truncate-text">
                        {{ getFullFileNamesAccordian(job, detail) }}
                      </span>
                      <div class="tooltip">
                        {{ getFullFileNamesAccordian(job, detail) }}
                      </div>
                    </div>
                  </td>
                  <td class="truncate-cell" style="width: 110px">{{ formatDate(job?.created_at).date }}/{{ formatDate(job?.created_at).time }}</td>
                  <td class="truncate-cell" style="text-align: left">
                    <div class="tooltip-container">
                      <span class="truncate-text">{{ getCreatedBy(job) }}</span>
                      <div class="tooltip">{{ getCreatedBy(job) }}</div>
                    </div>
                  </td>
                  <td>
                    <StatusBadge :status="getStatusForDetail(detail)" />
                  </td>
                  <td style="text-align: left">
                    <StatusBadge :status="getVasionStatusForDetail(detail)" />
                  </td>
                  <td>
                    <div class="job-actions-wrapper">
                      <button class="job-actions-trigger" style="z-index: -1 !important" @click.stop="showActionsMenu($event, detail, (currentPage - 1) * itemsPerPage + index + 1)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="6" r="2" fill="var(--primary-color)" />
                          <circle cx="12" cy="12" r="2" fill="var(--primary-color)" />
                          <circle cx="12" cy="18" r="2" fill="var(--primary-color)" />
                        </svg>
                      </button>
                      <div class="job-actions-menu" v-show="actionsDropdownJobFileId === (currentPage - 1) * itemsPerPage + index + 1" @click.stop>
                        <div v-if="selectedItem !== 'Pending'" class="job-action-item" @click.stop="handleView(detail, false)">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M21.544 11.045C21.848 11.471 22 11.685 22 12C22 12.316 21.848 12.529 21.544 12.955C20.178 14.871 16.689 19 12 19C7.31 19 3.822 14.87 2.456 12.955C2.152 12.529 2 12.315 2 12C2 11.684 2.152 11.471 2.456 11.045C3.822 9.129 7.311 5 12 5C16.69 5 20.178 9.13 21.544 11.045Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M15 12C15 11.2044 14.6839 10.4413 14.1213 9.87868C13.5587 9.31607 12.7956 9 12 9C11.2044 9 10.4413 9.31607 9.87868 9.87868C9.31607 10.4413 9 11.2044 9 12C9 12.7956 9.31607 13.5587 9.87868 14.1213C10.4413 14.6839 11.2044 15 12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          <span>{{ $t('job_history.actions.view') }}</span>
                        </div>
                        <div v-if="selectedItem !== 'Pending' && job?.status !== 'failed'" class="job-action-item" @click.stop="handleView(detail, true)">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M2.5 13.833L6 17.5L7.024 16.427M16.5 6.5L10.437 12.852M7.5 13.833L11 17.5L21.5 6.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          <span>{{ $t('job_history.actions.manual_validations') }}</span>
                        </div>
                        <div v-if="selectedItem !== 'Pending'" class="job-action-item" @click.stop="handleViewJobHistory(detail)">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 8V12L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          <span>{{ $t('job_history.actions.view_job_history') }}</span>
                        </div>
                        <!-- <div v-if="selectedItem !== 'Pending' && selectedItem !== 'Completed' && selectedItemManual !== 'Completed' && selectedItem !== 'Failed' && selectedItemManual !== 'Failed'" class="job-action-item" @click.stop="handleStartWorkflow(detail)">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M21.75 12C21.75 14.5859 20.7228 17.0658 18.8943 18.8943C17.0658 20.7228 14.5859 21.75 12 21.75C9.41414 21.75 6.93419 20.7228 5.10571 18.8943C3.27723 17.0658 2.25 14.5859 2.25 12C2.25 9.41414 3.27723 6.93419 5.10571 5.10571C6.93419 3.27723 9.41414 2.25 12 2.25C14.5859 2.25 17.0658 3.27723 18.8943 5.10571C20.7228 6.93419 21.75 9.41414 21.75 12Z" stroke="currentColor" stroke-width="1.2" />
                            <path d="M16.5 12L9.75 7.5V16.5L16.5 12Z" stroke="currentColor" />
                          </svg>
                          <span>{{ $t('job_history.actions.start_workflow') }}</span>
                        </div>
                        <div v-if="selectedItem !== 'Pending' && selectedItem !== 'Completed' && selectedItemManual !== 'Completed' && selectedItem !== 'Failed' && selectedItemManual !== 'Failed'" class="job-action-item" @click.stop="handleSendToVasion(detail, '')">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clip-path="url(#clip0_789_2107)">
                              <path d="M12 4C10.6739 4 9.40215 4.52678 8.46447 5.46447C7.52678 6.40215 7 7.67392 7 9V11H6C5.22 11 4.195 11.302 3.38 11.964C2.598 12.6 2 13.572 2 15C2 16.414 2.615 17.388 3.42 18.032C4.256 18.702 5.286 19 6 19C6.973 19 8.239 18.985 9.263 18.479C9.748 18.239 10.163 17.894 10.466 17.382C10.772 16.865 11 16.112 11 15V13.914L9.5 15.414L8.086 14L12 10.086L15.914 14L14.5 15.414L13 13.914V15C13 16.387 12.714 17.51 12.188 18.4C11.7066 19.2108 10.9971 19.8619 10.148 20.272C8.674 21 6.979 21 6.056 21H6C4.832 21 3.362 20.548 2.17 19.593C0.943 18.613 0 17.086 0 15C0 12.928 0.903 11.4 2.12 10.411C2.95554 9.73913 3.94538 9.28614 5 9.093V9C5 7.14348 5.7375 5.36301 7.05025 4.05025C8.36301 2.7375 10.1435 2 12 2C13.8565 2 15.637 2.7375 16.9497 4.05025C18.2625 5.36301 19 7.14348 19 9V9.093C20.0546 9.28614 21.0445 9.73913 21.88 10.411C23.099 11.401 24 12.928 24 15C24 17.076 23.093 18.614 21.795 19.607C20.529 20.575 18.93 21 17.5 21H13.5V19H17.5C18.57 19 19.721 18.675 20.58 18.018C21.407 17.386 22 16.424 22 15C22 13.572 21.402 12.6 20.62 11.964C19.804 11.302 18.78 11 18 11H17V9C17 7.67392 16.4732 6.40215 15.5355 5.46447C14.5979 4.52678 13.3261 4 12 4Z" fill="currentColor" stroke="white" stroke-width="0.8" />
                            </g>
                            <defs>
                              <clipPath id="clip0_789_2107">
                                <rect width="24" height="24" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          <span>{{ $t('job_history.actions.send_to_approval') }}</span>
                        </div>
                        <div v-if="selectedItem !== 'Pending' && selectedItem !== 'Completed' && selectedItemManual !== 'Completed' && selectedItem !== 'Failed' && selectedItemManual !== 'Failed'" class="job-action-item" @click.stop="handleSendToVasion(detail, 'Review')">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clip-path="url(#clip0_789_2107)">
                              <path d="M12 4C10.6739 4 9.40215 4.52678 8.46447 5.46447C7.52678 6.40215 7 7.67392 7 9V11H6C5.22 11 4.195 11.302 3.38 11.964C2.598 12.6 2 13.572 2 15C2 16.414 2.615 17.388 3.42 18.032C4.256 18.702 5.286 19 6 19C6.973 19 8.239 18.985 9.263 18.479C9.748 18.239 10.163 17.894 10.466 17.382C10.772 16.865 11 16.112 11 15V13.914L9.5 15.414L8.086 14L12 10.086L15.914 14L14.5 15.414L13 13.914V15C13 16.387 12.714 17.51 12.188 18.4C11.7066 19.2108 10.9971 19.8619 10.148 20.272C8.674 21 6.979 21 6.056 21H6C4.832 21 3.362 20.548 2.17 19.593C0.943 18.613 0 17.086 0 15C0 12.928 0.903 11.4 2.12 10.411C2.95554 9.73913 3.94538 9.28614 5 9.093V9C5 7.14348 5.7375 5.36301 7.05025 4.05025C8.36301 2.7375 10.1435 2 12 2C13.8565 2 15.637 2.7375 16.9497 4.05025C18.2625 5.36301 19 7.14348 19 9V9.093C20.0546 9.28614 21.0445 9.73913 21.88 10.411C23.099 11.401 24 12.928 24 15C24 17.076 23.093 18.614 21.795 19.607C20.529 20.575 18.93 21 17.5 21H13.5V19H17.5C18.57 19 19.721 18.675 20.58 18.018C21.407 17.386 22 16.424 22 15C22 13.572 21.402 12.6 20.62 11.964C19.804 11.302 18.78 11 18 11H17V9C17 7.67392 16.4732 6.40215 15.5355 5.46447C14.5979 4.52678 13.3261 4 12 4Z" fill="currentColor" stroke="white" stroke-width="0.8" />
                            </g>
                            <defs>
                              <clipPath id="clip0_789_2107">
                                <rect width="24" height="24" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          <span>{{ $t('job_history.actions.send_to_reviewed') }}</span>
                        </div>
                        <div v-if="selectedItem !== 'Pending' && selectedItem !== 'Completed' && selectedItemManual !== 'Completed' && selectedItem !== 'Failed' && selectedItemManual !== 'Failed'" class="job-action-item" @click.stop="handleSendToVasion(detail, 'Rejection')">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clip-path="url(#clip0_789_2107)">
                              <path d="M12 4C10.6739 4 9.40215 4.52678 8.46447 5.46447C7.52678 6.40215 7 7.67392 7 9V11H6C5.22 11 4.195 11.302 3.38 11.964C2.598 12.6 2 13.572 2 15C2 16.414 2.615 17.388 3.42 18.032C4.256 18.702 5.286 19 6 19C6.973 19 8.239 18.985 9.263 18.479C9.748 18.239 10.163 17.894 10.466 17.382C10.772 16.865 11 16.112 11 15V13.914L9.5 15.414L8.086 14L12 10.086L15.914 14L14.5 15.414L13 13.914V15C13 16.387 12.714 17.51 12.188 18.4C11.7066 19.2108 10.9971 19.8619 10.148 20.272C8.674 21 6.979 21 6.056 21H6C4.832 21 3.362 20.548 2.17 19.593C0.943 18.613 0 17.086 0 15C0 12.928 0.903 11.4 2.12 10.411C2.95554 9.73913 3.94538 9.28614 5 9.093V9C5 7.14348 5.7375 5.36301 7.05025 4.05025C8.36301 2.7375 10.1435 2 12 2C13.8565 2 15.637 2.7375 16.9497 4.05025C18.2625 5.36301 19 7.14348 19 9V9.093C20.0546 9.28614 21.0445 9.73913 21.88 10.411C23.099 11.401 24 12.928 24 15C24 17.076 23.093 18.614 21.795 19.607C20.529 20.575 18.93 21 17.5 21H13.5V19H17.5C18.57 19 19.721 18.675 20.58 18.018C21.407 17.386 22 16.424 22 15C22 13.572 21.402 12.6 20.62 11.964C19.804 11.302 18.78 11 18 11H17V9C17 7.67392 16.4732 6.40215 15.5355 5.46447C14.5979 4.52678 13.3261 4 12 4Z" fill="currentColor" stroke="white" stroke-width="0.8" />
                            </g>
                            <defs>
                              <clipPath id="clip0_789_2107">
                                <rect width="24" height="24" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          <span>{{ $t('job_history.actions.send_to_rejection') }}</span>
                        </div>
                        <div v-if="(selectedItem === 'Failed' || selectedItemManual === 'Failed') && detail?.resultDocument?.length > 0" class="job-action-item" @click.stop="handleSendToVasion(detail, 'Failed')">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clip-path="url(#clip0_789_2107)">
                              <path d="M12 4C10.6739 4 9.40215 4.52678 8.46447 5.46447C7.52678 6.40215 7 7.67392 7 9V11H6C5.22 11 4.195 11.302 3.38 11.964C2.598 12.6 2 13.572 2 15C2 16.414 2.615 17.388 3.42 18.032C4.256 18.702 5.286 19 6 19C6.973 19 8.239 18.985 9.263 18.479C9.748 18.239 10.163 17.894 10.466 17.382C10.772 16.865 11 16.112 11 15V13.914L9.5 15.414L8.086 14L12 10.086L15.914 14L14.5 15.414L13 13.914V15C13 16.387 12.714 17.51 12.188 18.4C11.7066 19.2108 10.9971 19.8619 10.148 20.272C8.674 21 6.979 21 6.056 21H6C4.832 21 3.362 20.548 2.17 19.593C0.943 18.613 0 17.086 0 15C0 12.928 0.903 11.4 2.12 10.411C2.95554 9.73913 3.94538 9.28614 5 9.093V9C5 7.14348 5.7375 5.36301 7.05025 4.05025C8.36301 2.7375 10.1435 2 12 2C13.8565 2 15.637 2.7375 16.9497 4.05025C18.2625 5.36301 19 7.14348 19 9V9.093C20.0546 9.28614 21.0445 9.73913 21.88 10.411C23.099 11.401 24 12.928 24 15C24 17.076 23.093 18.614 21.795 19.607C20.529 20.575 18.93 21 17.5 21H13.5V19H17.5C18.57 19 19.721 18.675 20.58 18.018C21.407 17.386 22 16.424 22 15C22 13.572 21.402 12.6 20.62 11.964C19.804 11.302 18.78 11 18 11H17V9C17 7.67392 16.4732 6.40215 15.5355 5.46447C14.5979 4.52678 13.3261 4 12 4Z" fill="currentColor" stroke="white" stroke-width="0.8" />
                          </g>
                            <defs>
                              <clipPath id="clip0_789_2107">
                                <rect width="24" height="24" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          <span>{{ $t('job_history.actions.send_to_failed') }}</span>
                        </div> -->
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
            <div v-else class="summary-loader-overlay">
              <div class="spinner"></div>
            </div>
          </table>
        </div>
      </div>

      <div class="pagination-container" v-if="job?.status !== 'inprogress'">
        <div class="pagination-controls">
          <button class="pagination-button" @click="previousPage" :disabled="currentPage === 1">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <span class="page-info">{{ $t('jobDetails.pagination.page') }} {{ currentPage }} {{ $t('jobDetails.pagination.of') }} {{ totalPages }}</span>
          <button class="pagination-button" @click="nextPage" :disabled="currentPage === totalPages">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
  <VasionConfirmationModal v-if="showVasionConfirmation" :show="showVasionConfirmation" @close="showVasionConfirmation = false" @confirm="confirmVasionAction" />
</template>

<script>
import StatusBadge from '../StatusBadge.vue'
import { post, get } from '../../utils/api.js'
import VasionConfirmationModal from '../VasionConfirmationModal/VasionConfirmationModal.vue'
import { showToast } from '../../utils/toast.js'
import { newFormatString } from '@/documentTraning/src/utils/utils'

export default {
  name: 'JobDetailsModal',
  components: {
    StatusBadge,
    VasionConfirmationModal,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    isBedrock: {
      type: Boolean,
      required: true,
    },
    job: {
      type: Object,
      required: true,
    },
    jobDetails: {
      type: Array,
      required: true,
    },
    selectedItem: {
      type: String,
      required: true,
    },
    selectedItemManual: {
      type: String,
      required: true,
    },
    jobHistoryPage: {
      type: Number,
      default: 1,
    },
    jobHistoryPageSize: {
      type: Number,
      default: 10,
    },
    jobHistoryDateRange: {
      type: Array,
      default: () => [],
    },
    jobHistorySelectedUserId: {
      type: String,
      default: '',
    },
    jobHistorySearch: {
      type: String,
      default: '',
    },
    jobHistoryIsManuallyValidated: {
      type: Number,
      default: 0,
    },
    isTextract: {
      type: Boolean,
      required: true,
    },
    from: {
      type: String,
      default: 'job-history', // 'dashboard' or 'job-history'
    },
  },
  data() {
    return {
      actionsDropdownJobFileId: null,
      currentPage: 1,
      itemsPerPage: 10,
      showVasionConfirmation: false,
      selectedDetail: null,
      lastAction: null,
      selectedSecondaryVasionStatus: '',
      paginatedDetails: [],
      isLoadingPaginatedDetails: false,
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.jobDetails.length / this.itemsPerPage)
    },
    processingTime() {
      if (this.job?.completion_time != null && this.job?.completion_time > 0) {
        const totalSeconds = Math.floor(this.job.completion_time)
        const minutes = Math.floor(totalSeconds / 60)
        const seconds = totalSeconds % 60
        return `${minutes} min ${seconds} sec`
      }

      if (!this.job?.completed_at || !this.job?.started_at) {
        return '0 min 0 sec'
      }

      const completed = new Date(this.job.completed_at)
      const created = new Date(this.job.started_at)
      const diffInSeconds = Math.floor((completed - created) / 1000)

      const minutes = Math.floor(diffInSeconds / 60)
      const seconds = diffInSeconds % 60

      return `${minutes} min ${seconds} sec`
    },
    filesWithErrors() {
      if (!this.job?.mx_job_files) {
        return []
      }
      return this.job.mx_job_files.filter((file) => file.error)
    },
    hasFileErrors() {
      return this.filesWithErrors.length > 0
    },
  },
  methods: {
    newFormatString,
    handleClose() {
      // Close job actions menu when modal closes
      this.actionsDropdownJobFileId = null
      this.$emit('close')
    },
    handleViewJobHistory(detail) {
      // Close the actions dropdown
      this.actionsDropdownJobFileId = null

      // Get actual document names from the job data
      const fileName = this.getFullFileNamesAccordian(this.job, detail)
      const resultDocumentName = this.getResultDocumentName(this.job, detail)

      // Emit event to parent component to show document history modal
      this.$emit('viewJobHistory', {
        jobId: this.job?.id,
        selectedResultDocument: {
          fileName: fileName || 'Unknown',
          resultDocumentName: resultDocumentName || 'Unknown',
        },
      })
    },
    handleModalClick(event) {
      // Close job actions menu when clicking outside
      if (event.target.classList.contains('modal-overlay')) {
        this.actionsDropdownJobFileId = null
        this.handleClose()
      }
    },
    formatDate(dateStr) {
      const dateObj = new Date(dateStr)
      const dateOptions = {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      }
      const timeOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }
      const date = dateObj.toLocaleDateString('en-US', dateOptions)
      const time = dateObj.toLocaleTimeString('en-US', timeOptions)
      return { date, time }
    },
    showTooltip(event, text) {
      const tooltip = document.createElement('div')
      tooltip.className = 'tooltip'
      tooltip.textContent = text
      document.body.appendChild(tooltip)

      const rect = event.target.getBoundingClientRect()
      const tooltipRect = tooltip.getBoundingClientRect()

      let left = rect.left
      let top = rect.bottom + 5

      // Adjust if tooltip would go off screen
      if (left + tooltipRect.width > window.innerWidth) {
        left = window.innerWidth - tooltipRect.width - 10
      }
      if (top + tooltipRect.height > window.innerHeight) {
        top = rect.top - tooltipRect.height - 5
      }

      tooltip.style.left = `${left}px`
      tooltip.style.top = `${top}px`
      tooltip.style.display = 'block'
      tooltip.style.opacity = '1'

      const hideTooltip = () => {
        tooltip.style.opacity = '0'
        setTimeout(() => {
          document.body.removeChild(tooltip)
        }, 200)
      }

      event.target.addEventListener('mouseleave', hideTooltip, { once: true })
    },
    getCreatedBy(job) {
      return `${job?.updated_by_user?.first_name} ${job?.updated_by_user?.last_name}`
    },
    getFullFileNamesAccordian(job, detail) {
      // const jobFileId = detail.jobFileId
      if (!job?.mx_job_result_documents || job?.mx_job_result_documents.length === 0) {
        return '---'
      }
      const result_documents = job?.mx_job_result_documents?.filter((file) => file.id === detail.mx_job_result_document_id)
      const uniqueFileNames = [...new Set(result_documents.map((doc) => doc.file_name).filter(Boolean))].join(', ')
      return uniqueFileNames || '---'
    },
    getResultDocumentName(job, detail) {
      // First try to get from detail.resultDocument (like getVasionStatusForDetail does)
      if (detail?.resultDocument && detail.resultDocument.length > 0) {
        const matchingDoc = detail.resultDocument.find((doc) => doc.id === detail.mx_job_result_document_id)
        if (matchingDoc) {
          // Try different property names
          return matchingDoc.result_document_name || matchingDoc.document_name_full || matchingDoc.file_name || '---'
        }
      }

      // Try to get from job.file_page_textract_details (like ViewDocument does)
      if (job?.file_page_textract_details && job.file_page_textract_details.length > 0) {
        const matchingDoc = job.file_page_textract_details.find((doc) => doc.mx_job_result_document_id === detail.mx_job_result_document_id)
        if (matchingDoc) {
          return matchingDoc.document_name_full || matchingDoc.result_document_name || matchingDoc.file_name || '---'
        }
      }

      // Fallback to job.mx_job_result_documents
      if (!job?.mx_job_result_documents || job?.mx_job_result_documents.length === 0) {
        return '---'
      }

      const result_documents = job?.mx_job_result_documents?.filter((file) => file.id === detail.mx_job_result_document_id)

      if (result_documents.length > 0) {
        const doc = result_documents[0]
        return doc.document_name_full || doc.result_document_name || doc.file_name || '---'
      }

      return '---'
    },
    getVasionStatusForDetail(detail) {
      if (!detail?.resultDocument) return '---'
      const matchingDoc = detail.resultDocument.find((doc) => doc.id === detail.mx_job_result_document_id)
      return matchingDoc?.vasion_status || '---'
    },
    getStatusForDetail(detail) {
      if (!detail?.resultDocument) return '---'
      const matchingDoc = detail.resultDocument.find((doc) => doc.id === detail.mx_job_result_document_id)
      return matchingDoc?.status || '---'
    },
    getStatusForIsCompleted(detail) {
      if (!detail?.resultDocument) return false
      const matchingDoc = detail.resultDocument.some((doc) => doc.status === 'completed')
      return matchingDoc ? true : false
    },
    showActionsMenu(event, detail, number) {
      event.stopPropagation()
      if (this.actionsDropdownJobFileId === number) {
        this.actionsDropdownJobFileId = null // close if already open
      } else {
        this.actionsDropdownJobFileId = number // open for this row
      }
      // ...positioning code if needed
    },
    handleView(detail, isManual) {
      if (this.selectedItem === 'Pending') {
        return
      }
      const tenantName = localStorage.getItem('url_name')

      // Get the document ID from the detail object
      const documentId = detail?.mx_job_result_document_id

      // Preserve current query parameters for returning to the same state
      const returnQuery = {
        page: this.jobHistoryPage || 1,
        pageSize: this.jobHistoryPageSize || 10,
        status: this.jobHistoryIsManuallyValidated ? this.selectedItemManual : this.selectedItem,
        is_manually_validated: this.jobHistoryIsManuallyValidated || 0,
      }
      if (this.jobHistoryDateRange && this.jobHistoryDateRange.length > 0) {
        returnQuery.dateRange = JSON.stringify(this.jobHistoryDateRange)
      }
      if (this.jobHistorySelectedUserId) {
        returnQuery.created_by = this.jobHistorySelectedUserId
      }
      if (this.jobHistorySearch) {
        returnQuery.search = this.jobHistorySearch
      }

      // Store state in sessionStorage for browser back button support
      sessionStorage.setItem('jobHistoryReturnState', JSON.stringify(returnQuery))

      this.$router.push({
        name: 'view-document',
        params: { tenantName },
        query: {
          jobId: this.job?.id,
          isManual,
          documentId,
          returnQuery: JSON.stringify(returnQuery),
          from: this.from, // Pass the source to ViewDocument
        },
      })
    },
    handleStartWorkflow(detail) {
      this.lastAction = 'startWorkflow'
      this.handleVasionAction(detail, false)
    },
    handleSendToVasion(detail, secondaryVasionStatus) {
      this.lastAction = 'sendToVasion'
      this.handleVasionAction(detail, true, secondaryVasionStatus)
    },
    async handleVasionAction(detail, saveToVasion, secondaryVasionStatus) {
      try {
        // Check if vasion_status is completed and saveToVasion is true
        if (detail?.resultDocumentSelected?.vasion_status === 'completed') {
          this.selectedDetail = detail
          this.showVasionConfirmation = true
          this.selectedSecondaryVasionStatus = secondaryVasionStatus || ''
          return
        }
        // Make API call
        const docId = detail.resultDocumentSelected?.id || detail.mx_job_result_document_id || detail.id
        const payload = {
          job_id: this.job.id,
          result_doc_ids: [docId],
          save_to_vasion: saveToVasion,
        }
        if (secondaryVasionStatus) {
          payload.secondary_vasion_status = secondaryVasionStatus
        } else if (this.selectedSecondaryVasionStatus) {
          payload.secondary_vasion_status = this.selectedSecondaryVasionStatus
        }
        const response = await post('/v1/vasion/save-specific-documents', payload)
        if (response?.data?.code === 200) {
          showToast({
            title: 'Success',
            message: saveToVasion ? `Document sent to Vasion${payload.secondary_vasion_status ? ' (' + payload.secondary_vasion_status + ')' : ''} successfully` : 'Workflow started successfully',
            type: 'success',
          })
          this.$emit('refresh')
          this.handleClose()
        }
      } catch (error) {
        console.error('Error:', error)
        showToast({
          title: 'Error',
          message: error.response?.data?.message || `Operation failed${secondaryVasionStatus ? ' for ' + secondaryVasionStatus + ' Storage' : ''}`,
          type: 'error',
        })
      }
    },
    async confirmVasionAction() {
      if (this.selectedDetail) {
        try {
          const docId = this.selectedDetail.resultDocumentSelected?.id || this.selectedDetail.mx_job_result_document_id || this.selectedDetail.id
          const payload = {
            job_id: this.job.id,
            result_doc_ids: [docId],
            save_to_vasion: this.lastAction === 'sendToVasion' ? true : false,
          }
          if (this.selectedSecondaryVasionStatus) {
            payload.secondary_vasion_status = this.selectedSecondaryVasionStatus
          }
          const response = await post('/v1/vasion/save-specific-documents', payload)
          if (response?.data?.code === 200) {
            showToast({
              title: 'Success',
              message: `Document sent to Vasion${payload.secondary_vasion_status ? ' (' + payload.secondary_vasion_status + ')' : ''} successfully`,
              type: 'success',
            })
            this.$emit('refresh')
            this.handleClose()
          }
        } catch (error) {
          console.error('Error in confirmVasionAction:', error)
          showToast({
            title: 'Error',
            message: error.response?.data?.message || 'Operation failed',
            type: 'error',
          })
        } finally {
          this.showVasionConfirmation = false
          this.selectedDetail = null
          this.selectedSecondaryVasionStatus = ''
        }
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    getCategoryNames(job) {
      if (!job?.mx_file_page_textract_details || job.mx_file_page_textract_details.length === 0) return '---'
      const uniqueCategories = [...new Set(job.mx_file_page_textract_details.map((file) => file.tenant_document_type?.category?.category_name).filter(Boolean))].join(', ')
      if (uniqueCategories.length <= 20) return uniqueCategories
      return uniqueCategories.substring(0, 20) + '...'
    },
    getCategoryNamesTooltip(job) {
      if (!job?.mx_file_page_textract_details || job.mx_file_page_textract_details.length === 0) return '---'
      const uniqueCategories = [...new Set(job.mx_file_page_textract_details.map((file) => file.tenant_document_type?.category?.category_name).filter(Boolean))].join(', ')
      return uniqueCategories
    },
    getDepartmentNames(job) {
      if (!job?.mx_file_page_textract_details || job.mx_file_page_textract_details.length === 0) return '---'
      const uniqueDepartments = [...new Set(job.mx_file_page_textract_details.map((file) => file.tenant_document_type?.department?.department_name).filter(Boolean))].join(', ')
      if (uniqueDepartments.length <= 20) return uniqueDepartments
      return uniqueDepartments.substring(0, 20) + '...'
    },
    getDepartmentNamesTooltip(job) {
      if (!job?.mx_file_page_textract_details || job.mx_file_page_textract_details.length === 0) return '---'
      const uniqueDepartments = [...new Set(job.mx_file_page_textract_details.map((file) => file.tenant_document_type?.department?.department_name).filter(Boolean))].join(', ')
      return uniqueDepartments
    },
    getFullDepartmentNames(job) {
      if (!job?.mx_file_page_textract_details || job.mx_file_page_textract_details.length === 0) return '---'
      return [...new Set(job.mx_file_page_textract_details.map((file) => file.tenant_document_type?.department?.department_name).filter(Boolean))].join(', ')
    },
    getFullDocumentTypeNames(job) {
      if (!job?.mx_file_page_textract_details || job.mx_file_page_textract_details.length === 0) return '---'
      const uniqueDocumentTypes = [
        ...new Set(
          job.mx_file_page_textract_details
            .map((file) => file.tenant_document_type?.document_type?.document_type_name)
            .filter(Boolean)
            .map((name) => name.replace(/_draft$/i, ''))
        ),
      ].join(', ')
      return uniqueDocumentTypes
    },
    getFullDocumentTypeNamesTooltip(job) {
      if (!job?.mx_file_page_textract_details || job.mx_file_page_textract_details.length === 0) return '---'
      const uniqueDocumentTypes = [
        ...new Set(
          job.mx_file_page_textract_details
            .map((file) => file.tenant_document_type?.document_type?.document_type_name)
            .filter(Boolean)
            .map((name) => name.replace(/_draft$/i, ''))
        ),
      ].join(', ')
      return uniqueDocumentTypes
    },
    getFileNames(job) {
      if (!job?.mx_job_files || job?.mx_job_files?.length === 0) return '---'
      const uniqueFileNames = [...new Set(job?.mx_job_files?.map((file) => file.file_name).filter(Boolean))].join(', ')
      if (uniqueFileNames.length <= 20) return uniqueFileNames
      return uniqueFileNames.substring(0, 20) + '...'
    },
    getFileNamesTooltip(job) {
      if (!job?.mx_job_files || job?.mx_job_files?.length === 0) return '---'
      const uniqueFileNames = [...new Set(job?.mx_job_files?.map((file) => file.file_name).filter(Boolean))].join(', ')
      return uniqueFileNames
    },
    getFullFileNames(job) {
      if (!job?.mx_job_files || job.mx_job_files?.length === 0) return '---'
      return [...new Set(job?.mx_job_files?.map((file) => file.file_name).filter(Boolean))].join(', ')
    },
    async loadPaginatedDetails() {
      // Async method to load and process paginated details
      this.isLoadingPaginatedDetails = true
      try {
        const enrichedDetails = await Promise.all(
          this.jobDetails?.map(async (item) => {
            let isDeleted = false

            if (Array.isArray(item?.resultDocument)) {
              const matchedDoc = item.resultDocument.find((elem) => elem.id === item.mx_job_result_document_id)

              // Case: mx_job_result_document_id = 0 → call API (group by mx_job_file_id)
              if (item.mx_job_result_document_id === 0 && this.job?.id && item.jobFileId) {
                const res = await get(`/v1/mx-file-page-textract-details/is-no-match-deleted?mx_job_id=${this.job?.id}&mx_job_file_id=${item.jobFileId}`)
                isDeleted = res.data
              }
              // Normal match case
              if (matchedDoc) {
                isDeleted = matchedDoc.is_deleted
              }
            }

            return {
              ...item,
              is_deleted: isDeleted,
            }
          })
        )

        const sortedDetails = enrichedDetails.sort((a, b) => {
          if (a.id == null && b.id == null) return 0
          if (a.id == null) return 1
          if (b.id == null) return -1
          return a.id - b.id
        })
        const start = (this.currentPage - 1) * this.itemsPerPage
        const end = start + this.itemsPerPage
        this.paginatedDetails = sortedDetails.slice(start, end)
      } catch (error) {
        console.error('Error loading paginated details:', error)
        this.paginatedDetails = []
      } finally {
        this.isLoadingPaginatedDetails = false
      }
    },
  },
  watch: {
    show(isVisible) {
      if (isVisible) {
        this.currentPage = 1
        this.loadPaginatedDetails()
      } else {
        // Close job actions menu when modal closes
        this.actionsDropdownJobFileId = null
      }
    },
    jobDetails: {
      handler() {
        this.loadPaginatedDetails()
      },
      deep: true,
    },
    currentPage() {
      this.loadPaginatedDetails()
    },
    actionsDropdownJobFileId() {
      // Watch for changes in actionsDropdownJobFileId
    },
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.5);
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  width: 90%;
  max-width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  z-index: 100000;
  isolation: isolate;
  margin: auto;
}

.close-button {
  position: absolute;
  right: 16px;
  top: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
  z-index: 1;
}

.close-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.modal-header {
  padding: 6px;
  border-bottom: 1px solid #e8e8e8;
}

.modal-title {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  width: 100%;
  text-align: center;
}

.modal-body {
  padding: 6px;
  flex: 1;
  overflow-y: auto;
}

.table-container {
  overflow-x: auto;
  position: relative;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.custom-table-header {
  background-color: #f8f9fa;
}

.custom-table-row th {
  padding: 6px;
  text-align: left;
  font-weight: 600;
  color: var(--primary-color);
  border-bottom: 1px solid #e8e8e8;
  white-space: nowrap;
  font-size: 14px;
}

.custom-table-body td {
  padding: 6px;
  border-bottom: 1px solid #e8e8e8;
}

.tooltip-container {
  position: relative;
  width: 100%;
  display: block;
}

.tooltip-container:hover .tooltip {
  display: block;
  visibility: visible;
  opacity: 1;
}

.tooltip {
  display: none;
  position: fixed;
  background-color: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 999999;
  opacity: 0;
  transition: opacity 0.2s;
  white-space: normal;
  width: max-content;
  max-width: 300px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  word-break: break-word;
  pointer-events: none;
}

.truncate-cell {
  max-width: 150px;
  position: relative;
  overflow: hidden;
  font-size: 16px;
}

.truncate-text {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
  width: 100%;
}

.custom-table td {
  /* padding: 12px; */
  border-bottom: 1px solid #e8e8e8;
  vertical-align: middle;
  position: relative;
}

.custom-table td:last-child {
  min-width: 80px;
  width: 80px;
  text-align: center;
  padding-right: 20px;
}

.actions-menu {
  position: relative;
  display: inline-block;
  z-index: 100001;
  isolation: isolate;
  width: 100%;
}

.actions-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  position: relative;
  z-index: 100001;
}

.actions-button:hover {
  background-color: rgba(22, 72, 99, 0.1);
}

.actions-dropdown {
  position: fixed;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  box-shadow: 0 4px 13.1px rgba(0, 0, 0, 0.1);
  z-index: 100002;
  min-width: 200px;
  padding: 6px 0;
  display: block;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  cursor: pointer;
  color: #32393e;
  font-family: Archivo;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  transition: background-color 0.2s;
  white-space: nowrap;
  position: relative;
  z-index: 100003;
}

.action-item:hover {
  background-color: #f5f5f5;
}

.action-item svg {
  width: 20px;
  height: 20px;
  color: var(--primary-color);
  flex-shrink: 0;
}

.action-item span {
  white-space: nowrap;
}

/* Remove the hover display rules since we're using v-if */
.actions-menu:hover .actions-dropdown,
.actions-dropdown:hover {
  display: none;
}

/* Remove the backdrop since we're using fixed positioning */
.actions-dropdown::before {
  display: none;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6px;
  border-top: 1px solid #e8e8e8;
  background: white;
  position: sticky;
  bottom: 0;
  z-index: 1;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pagination-button {
  background: none;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background-color: #f5f5f5;
  border-color: var(--primary-color);
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-button svg {
  width: 20px;
  height: 20px;
  color: var(--primary-color);
}

.page-info {
  font-family: Archivo;
  font-size: 11px;
  color: #32393e;
  min-width: 100px;
  text-align: center;
}

.highlighted-row {
  background-color: rgba(var(--primary-color-rgb), 0.2);
  border: 2px solid var(--primary-color);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.normal-row {
  background-color: rgba(68, 68, 68, 0.2);
  cursor: pointer;
  transition: background-color 0.2s ease;
  z-index: 1;
}

.highlighted-row:hover {
  background-color: #e6f0ff;
}

.normal-row:hover {
  background-color: rgba(68, 68, 68, 0.3);
}

.sticky-row {
  position: sticky;
  top: 0;
  z-index: 1;
}

.job-actions-wrapper {
  /* position: relative; */
  display: inline-block;
  width: 100%;
  z-index: 1000;
}

.job-actions-trigger {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  width: 100%;
  z-index: 1;
}

.job-actions-trigger:hover {
  background-color: rgba(22, 72, 99, 0.1);
}

.job-actions-menu {
  position: fixed;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  box-shadow: 0 4px 13.1px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  padding: 6px 0;
  z-index: 9002;
  margin-top: 5px;
}

.job-action-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  cursor: pointer;
  color: #32393e;
  font-family: Archivo;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.job-action-item:hover {
  background-color: #f5f5f5;
}

.job-action-item svg {
  width: 20px;
  height: 20px;
  color: var(--primary-color);
  flex-shrink: 0;
}

.job-action-item span {
  white-space: nowrap;
}

/* Ensure the dropdown is not clipped */
.table-container,
.custom-table,
.custom-table-body,
tr,
td {
  overflow: visible !important;
}

/* Raise stacking context for rows */
.normal-row,
.highlighted-row {
  /* position: relative; */
  /* z-index: 200; */
}

/* Job Info Section Styles */
.job-info-section {
  margin-bottom: 24px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: transform 0.2s;
}

.info-item:hover {
  transform: translateY(-2px);
}

.info-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e3f2fd;
  border-radius: 8px;
  color: var(--primary-color);
  flex-shrink: 0;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.info-label {
  color: #5c656c;
  font-family: Archivo;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.info-value {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 18px;
  font-weight: 600;
}

.message-section {
  border-radius: 8px;
  padding: 12px;
  margin-top: 16px;
}

.message-section.error {
  background: #fff5f5;
  border: 1px solid #fed7d7;
}

.message-section.success {
  background: #f0fff4;
  border: 1px solid #c6f6d5;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Archivo;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.message-section.error .message-header {
  color: #e53e3e;
}

.message-section.success .message-header {
  color: #38a169;
}

.message-content {
  font-family: Archivo;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
}

.message-section.error .message-content {
  color: #5c656c;
}

.message-section.success .message-content {
  color: #38a169;
}

.message-content ul,
.message-content ol {
  margin: 8px 0;
  padding-left: 24px;
}

.message-content ul li,
.message-content ol li {
  margin: 4px 0;
  line-height: 1.6;
}

.message-content p {
  margin: 8px 0 4px 0;
}

.file-errors-section {
  margin-top: 16px;
}

.file-error-item {
  margin: 8px 0;
  padding: 4px 0;
}

.summary-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  /* transparent white overlay */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.spinner {
  border: 6px solid #f3f3f3;
  border-top: 6px solid #007bff;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
